import * as THREE from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';
import { OutlineShaderFrag, OutlineShaderVert } from './Shaders/OutlineShader';

class Machine {
    private object3D: THREE.Object3D;
    private baseColor: number = 0x5c5c5c;
    private labelDiv: HTMLDivElement;
    private onClickLabel: CSS2DObject;
    private machineId: string = '';
    private isSelected: boolean = false;
    private onClickCallback?: (machineId: string) => void;
    private pulsingAnimationId?: number; // Add animation frame tracking

    private uniforms: any = {
        color: { value: new THREE.Color(0xffffff) },
        outlineColor: { value: new THREE.Color(0x68696c) },
        thickness: { value: 0.07 }
    };

    constructor() {
        this.object3D = new THREE.Object3D();
        const geometry = new THREE.BoxGeometry();

        const outlineMaterial = new THREE.ShaderMaterial({
            fragmentShader: OutlineShaderFrag(),
            vertexShader: OutlineShaderVert(),
            uniforms: this.uniforms,
        });

        const cube = new THREE.Mesh(geometry, outlineMaterial);
        cube.scale.set(1, 0.4, 1);
        this.object3D.add(cube);
        cube.userData["onHover"] = this;
        cube.userData["onClick"] = this;

        this.labelDiv = document.createElement('div');
        this.labelDiv.className = 'label';
        this.labelDiv.style.backgroundColor = 'rgba(80, 80, 80, 0.9)';
        this.labelDiv.style.color = '#ffffff';
        this.labelDiv.style.fontWeight = 'bold';
        this.labelDiv.style.padding = '4px 8px';
        this.labelDiv.style.border = '2px solid #cccccc';
        this.labelDiv.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.5)';
        this.onClickLabel = new CSS2DObject(this.labelDiv);
        this.onClickLabel.visible = false;
        this.onClickLabel.element.style.color = '#9d9b9f';
        this.object3D.add(this.onClickLabel);
        this.onClickLabel.center.set(0.5, 0);
        this.onClickLabel.position.set(0, 2, 0);
    }

    public getObject3D() {
        return this.object3D;
    }

    public setPosition(x: number, y: number, z: number) {

        this.object3D.position.set(x, y, z);
    }

    public getPosition() {
        return this.object3D.position;
    }

    public resetColor() {
        this.uniforms.color.value.set(new THREE.Color(this.baseColor));
    }

    public setColor(color: number) {
        this.uniforms.color.value.set(new THREE.Color(color));
    }

    public setLabel(label: string) {
        this.machineId = label;
        this.labelDiv.textContent = this.machineId;
    }

    public setInnerHtml(html: string) {
        this.labelDiv.innerHTML = html;
    }

    public resetLabelToId(): void {
        this.labelDiv.textContent = this.machineId;
    }

    public onHover() {
        this.onClickLabel.visible = true;
    }

    public onHoverOut() {
        this.onClickLabel.visible = false;
    }

    public onClick(isVisible: boolean) {
        this.onClickLabel.visible = isVisible;
    }

    public getMachineId(): string {
        return this.machineId;
    }

    public setSelected(selected: boolean) {
        this.isSelected = selected;
        if (selected) {
            // Make it really pop with bright cyan outline and pulsing effect
            this.uniforms.outlineColor.value.set(new THREE.Color(0x00ffff)); // Bright cyan outline
            this.uniforms.thickness.value = 0.25; // Much thicker outline for maximum visibility

            // Make the machine really stand out with bright color
            this.uniforms.color.value.set(new THREE.Color(0xffffff)); // Bright white color

            // Don't show label when selected - keep it hidden for clean look
            this.onClickLabel.visible = false;

            // Add pulsing animation effect
            this.addPulsingEffect();
        } else {
            // Reset to normal
            this.uniforms.outlineColor.value.set(new THREE.Color(0x68696c));
            this.uniforms.thickness.value = 0.07;
            this.uniforms.color.value.set(new THREE.Color(this.baseColor));

            // Keep label hidden
            this.onClickLabel.visible = false;

            // Remove pulsing effect
            this.removePulsingEffect();
        }
    }

    public setSearchHighlight(highlighted: boolean) {
        if (highlighted) {
            // Don't override selection highlighting if machine is selected
            if (this.isSelected) {
                return;
            }

            // Special highlighting for search results
            this.uniforms.outlineColor.value.set(new THREE.Color(0x00ff88)); // Bright green outline
            this.uniforms.thickness.value = 0.18; // Extra thick outline
            this.uniforms.color.value.set(new THREE.Color(0xe8ffe8)); // Light green tint

            // Make the label more visible
            this.onClickLabel.visible = true;
            this.labelDiv.style.backgroundColor = 'rgba(0, 255, 136, 0.9)';
            this.labelDiv.style.color = '#000000';
            this.labelDiv.style.fontWeight = 'bold';
            this.labelDiv.style.padding = '4px 8px';
            this.labelDiv.style.borderRadius = '4px';
            this.labelDiv.style.border = '2px solid #00ff88';
            this.labelDiv.style.boxShadow = '0 2px 8px rgba(0, 255, 136, 0.5)';
        } else {
            // Don't reset if machine is selected (let selection styling take precedence)
            if (this.isSelected) {
                return;
            }

            // Reset search highlighting
            this.uniforms.outlineColor.value.set(new THREE.Color(0x68696c));
            this.uniforms.thickness.value = 0.07;
            this.uniforms.color.value.set(new THREE.Color(this.baseColor));

            this.onClickLabel.visible = false;
            this.labelDiv.style.backgroundColor = 'transparent';
            this.labelDiv.style.color = '#9d9b9f';
            this.labelDiv.style.fontWeight = 'normal';
            this.labelDiv.style.padding = '0';
            this.labelDiv.style.borderRadius = '0';
            this.labelDiv.style.border = 'none';
            this.labelDiv.style.boxShadow = 'none';
        }
    }

    public setOnClickCallback(callback: (machineId: string) => void) {
        this.onClickCallback = callback;
    }

    public handleClick() {
        if (this.onClickCallback) {
            this.onClickCallback(this.machineId);
        }
    }

    private addPulsingEffect(): void {
        if (this.pulsingAnimationId) {
            // Already pulsing, don't start another animation
            return;
        }

        const startTime = Date.now();
        const pulseSpeed = 2; // Controls pulsing speed
        const minThickness = 0.2;
        const maxThickness = 0.35;
        const minOpacity = 0.7;
        const maxOpacity = 1.0;

        const animate = () => {
            if (!this.isSelected) {
                // Stop animation if machine is no longer selected
                this.pulsingAnimationId = undefined;
                return;
            }

            const elapsed = (Date.now() - startTime) / 1000;
            const pulse = (Math.sin(elapsed * pulseSpeed) + 1) / 2; // Normalized sine wave 0-1

            // Animate outline thickness
            this.uniforms.thickness.value = minThickness + (maxThickness - minThickness) * pulse;

            // Animate color opacity for subtle breathing effect
            const opacity = minOpacity + (maxOpacity - minOpacity) * pulse;
            const currentColor = this.uniforms.color.value;
            currentColor.setRGB(opacity, opacity, opacity);

            // Animate outline color intensity
            const outlineIntensity = 0.5 + 0.5 * pulse;
            this.uniforms.outlineColor.value.setRGB(0, outlineIntensity, outlineIntensity); // Cyan pulsing

            this.pulsingAnimationId = requestAnimationFrame(animate);
        };

        this.pulsingAnimationId = requestAnimationFrame(animate);
    }

    private removePulsingEffect(): void {
        if (this.pulsingAnimationId) {
            cancelAnimationFrame(this.pulsingAnimationId);
            this.pulsingAnimationId = undefined;
        }
    }

    public dispose(): void {
        // Clean up animation when machine is destroyed
        this.removePulsingEffect();

        // Dispose of Three.js objects
        this.object3D.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                if (child.geometry) child.geometry.dispose();
                if (child.material) {
                    if (Array.isArray(child.material)) {
                        child.material.forEach(material => material.dispose());
                    } else {
                        child.material.dispose();
                    }
                }
            }
        });
    }

}

export default Machine;